import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Opens a specific transaction when a report links here via
 * `navigate(route, { state: { viewTransactionId } })`.
 *
 * Wire into any transaction list page:
 *   useOpenFromReport(invoices, handleView);   // or openEdit, etc.
 *
 * It waits until the records are loaded, finds the one whose `id` matches the
 * incoming `viewTransactionId`, calls `openFn(record)`, then clears the history
 * state so a refresh / back-nav doesn't re-trigger it. Fires once per arrival.
 *
 * @param {Array} records - the loaded list of transactions (each with an `id`)
 * @param {Function} openFn - opens one record (e.g. view/edit mode)
 * @param {object} [opts]
 * @param {string} [opts.idField='id'] - match field on each record
 */
export default function useOpenFromReport(records, openFn, opts = {}) {
  const location = useLocation();
  const idField = opts.idField || 'id';
  const handledRef = useRef(null);

  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    if (handledRef.current === targetId) return; // already handled this arrival
    if (!Array.isArray(records) || records.length === 0) return;

    const record = records.find(r => r && r[idField] === targetId);
    if (!record) return;

    handledRef.current = targetId;
    openFn(record);
    // Clear so a refresh or back navigation doesn't reopen it.
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, records]);
}
